import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { debounce } from "lodash";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useAPI from "../../../api/hooks/useAPI";
import { dashboardKeys } from "../../../api/queryKeys";
import { Toast } from "../../../components/Toast";
import { EnvFeatureFlagContext } from "../../../contexts/EnvFeaturesContext";
import {
  DEFAULT_WIDGETS,
  GRID_CONFIG,
  getAvailableWidgets,
} from "../custom_dashboard/widgetDefinitions";

const buildSharedLayouts = (widgets, existingLayouts) => {
  const cols = GRID_CONFIG.cols.lg || 12;
  const saved = new Map(
    (existingLayouts?.lg || []).map((layout) => [layout.i, layout])
  );

  const sharedLayout = widgets.map((widget, index) => {
    const defaultLayout = {
      x: (index * 2) % cols,
      y: Math.floor(index / (cols / 2)) * widget.h,
      w: Math.min(widget.w, cols),
      h: widget.h,
      i: widget.id,
      minH: widget.minH || 2,
      minW: widget.minW || 2,
      maxH: widget.maxH || 30,
      maxW: widget.maxW || 12,
      static: widget.static || false,
    };
    const savedLayout = saved.get(widget.id);
    return savedLayout
      ? { ...defaultLayout, ...savedLayout, i: widget.id }
      : defaultLayout;
  });
  const smallLayout = sharedLayout.map((layout) => {
    return {
      ...layout,
      w: Math.max(layout.w, 12),
    };
  });
  return {
    lg: sharedLayout,
    md: sharedLayout,
    sm: smallLayout,
    xs: smallLayout,
  };
};

export const useDashboard = () => {
  const { saveDashboard, loadDashboard, trackEvent } = useAPI();
  const queryClient = useQueryClient();
  const [mounted, setMounted] = useState(false);
  const { postureTrendChart } = useContext(EnvFeatureFlagContext);

  // Set mounted on component mount
  useEffect(() => {
    setMounted(true);
  }, []);

  // React Query for loading dashboard
  const {
    data: dashboardData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: dashboardKeys.all,
    queryFn: loadDashboard,
    select: (response) => {
      if (response?.data) {
        const parsed =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;
        return parsed;
      }
      const defaultData = {
        widgets: DEFAULT_WIDGETS,
        layouts: {},
      };
      return defaultData;
    },
  });

  // React Query for saving dashboard
  const {
    mutate: saveDashboardMutation,
    isPending: isSaving,
    isSuccess: isSaved,
  } = useMutation({
    mutationFn: (data) => {
      return saveDashboard(data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
    onError: (error) => {
      Toast.error("Error saving dashboard");
    },
  });

  // Memoized values
  const widgets = useMemo(() => {
    return dashboardData?.widgets || DEFAULT_WIDGETS;
  }, [dashboardData?.widgets]);

  const layouts = useMemo(
    () => dashboardData?.layouts || {},
    [dashboardData?.layouts]
  );

  const generateLayouts = useMemo(
    () => buildSharedLayouts(widgets, layouts),
    [widgets, layouts]
  );

  // Create debounced save function with useRef to persist across re-renders
  const debouncedSaveRef = useRef(null);

  // Update debounced function when saveDashboardMutation changes
  useEffect(() => {
    debouncedSaveRef.current = debounce((dashboardData) => {
      if (!mounted) {
        return;
      }
      trackEvent("User re-ordered dashboard widgets");
      saveDashboardMutation(dashboardData);
    }, 1000); // Wait 1 second after last layout change

    // Cleanup function
    return () => {
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
    };
  }, [saveDashboardMutation]);

  // Handle layout changes with debouncing
  const onLayoutChange = useCallback(
    (currentLayout, allLayouts) => {
      // Only save the lg layout as our master layout, since all breakpoints use the same layout
      const updatedDashboardData = {
        layouts: { lg: allLayouts.lg },
        widgets,
      };

      // Use debounced save to reduce API calls
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current(updatedDashboardData);
      }
    },
    [widgets, mounted]
  );

  // Toggle widget (add or remove based on checkbox state)
  const toggleWidget = useCallback(
    (widgetId, isChecked) => {
      let newWidgets;

      if (isChecked) {
        const availableWidgets = getAvailableWidgets(postureTrendChart);
        const widgetToAdd = availableWidgets.find((w) => w.id === widgetId);
        if (widgetToAdd) {
          newWidgets = [...widgets, widgetToAdd];
        } else {
          return;
        }
      } else {
        newWidgets = widgets.filter((w) => w.id !== widgetId);
      }

      const updatedLayouts = buildSharedLayouts(newWidgets, layouts);
      const updatedDashboardData = {
        layouts: updatedLayouts,
        widgets: newWidgets,
      };

      // Cancel any pending layout saves and save widget changes immediately
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
      trackEvent("User added or removed dashboard widget");
      saveDashboardMutation(updatedDashboardData);
    },
    [widgets, layouts, saveDashboardMutation, postureTrendChart]
  );

  // Save dashboard with specific widgets (for submit button approach) - IMMEDIATE save
  const saveDashboardWithWidgets = useCallback(
    (newWidgets) => {
      trackEvent("User saved changed dashboard view");
      const updatedLayouts = buildSharedLayouts(newWidgets, layouts);
      const updatedDashboardData = {
        layouts: updatedLayouts,
        widgets: newWidgets,
      };

      // Save widget changes immediately without debouncing
      saveDashboardMutation(updatedDashboardData);
    },
    [layouts, saveDashboardMutation]
  );

  return {
    // State
    mounted,
    widgets,
    layouts,
    generateLayouts,
    isLoading,
    isError,
    isSaving,
    isSaved,
    // Actions
    onLayoutChange,
    toggleWidget,
    saveDashboardWithWidgets,
  };
};
